import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useMobileNav } from "@/contexts/MobileNavContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, Share2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useRef } from "react";
import { PrescriptionPDFContent } from "@/components/prescription/PrescriptionPDFContent";
import { useExportToPDF } from "@/hooks/useExportToPDF";

const itemTypeLabels: Record<string, string> = {
  medication: "Medicamento",
  supplement: "Suplemento",
  vitamin: "Vitamina",
  other: "Outro",
};

export default function PrescriptionDetail() {
  const { prescriptionId } = useParams();
  const navigate = useNavigate();
  const pdfRef = useRef<HTMLDivElement>(null);
  const { exportToPDF, isExporting } = useExportToPDF(pdfRef);
  const { setShowMobileNav } = useMobileNav();

  // Ocultar o menu mobile ao montar e restaurar ao desmontar
  useEffect(() => {
    setShowMobileNav(false);
    return () => setShowMobileNav(true);
  }, [setShowMobileNav]);

  const { data: prescription, isLoading } = useQuery({
    queryKey: ["prescription", prescriptionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prescriptions")
        .select(`
          *,
          patients_onboarding(full_name, data_nascimento, cpf),
          prescription_items(*)
        `)
        .eq("id", prescriptionId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!prescription) {
    return <div>Prescrição não encontrada</div>;
  }

  const handleExportPDF = async () => {
    await exportToPDF(
      `Prescricao_${prescription.patients_onboarding.full_name}_${format(
        new Date(prescription.created_at),
        "dd-MM-yyyy"
      )}.pdf`
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/aplicativo/prescription")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Detalhes da Prescrição</h1>
            <p className="text-muted-foreground">
              Criada em {format(new Date(prescription.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Exportando..." : "Exportar PDF"}
          </Button>
          <Button variant="outline">
            <Share2 className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-teal-200">
          <CardHeader>
            <CardTitle className="text-teal-700 dark:text-teal-300">Dados do Paciente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <span className="text-sm text-muted-foreground">Nome:</span>
              <p className="font-semibold">{prescription.patients_onboarding.full_name}</p>
            </div>
            {prescription.patients_onboarding.cpf && (
              <div>
                <span className="text-sm text-muted-foreground">CPF:</span>
                <p className="font-semibold">{prescription.patients_onboarding.cpf}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-cyan-200">
          <CardHeader>
            <CardTitle className="text-cyan-700 dark:text-cyan-300">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              className={
                prescription.status === "completed"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
              }
            >
              {prescription.status === "completed" ? "Concluída" : "Rascunho"}
            </Badge>
          </CardContent>
        </Card>
      </div>

      <Card className="border-teal-200">
        <CardHeader>
          <CardTitle className="text-teal-700 dark:text-teal-300">Itens Prescritos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {prescription.prescription_items.map((item: any) => (
              <div
                key={item.id}
                className="p-4 rounded-lg border border-teal-200 dark:border-teal-800 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <Badge className="mb-2 bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                      {itemTypeLabels[item.item_type] || item.item_type}
                    </Badge>
                    <h4 className="font-semibold text-lg">{item.name}</h4>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {item.dosage && (
                    <div>
                      <span className="text-muted-foreground">Posologia:</span>
                      <p className="font-medium">{item.dosage}</p>
                    </div>
                  )}
                  {item.frequency && (
                    <div>
                      <span className="text-muted-foreground">Frequência:</span>
                      <p className="font-medium">{item.frequency}</p>
                    </div>
                  )}
                  {item.duration && (
                    <div>
                      <span className="text-muted-foreground">Prazo:</span>
                      <p className="font-medium">{item.duration}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hidden PDF Content */}
      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={pdfRef}>
          <PrescriptionPDFContent
            prescription={{
              id: prescription.id,
              patient: prescription.patients_onboarding,
              professional: {},
              items: prescription.prescription_items,
              created_at: prescription.created_at,
            }}
          />
        </div>
      </div>
    </div>
  );
}
